import React, { useState, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import moment from 'moment';
import isNull from 'lodash/isNull';
import isEmpty from 'lodash/isEmpty';
import isUndefined from 'lodash/isUndefined';
import isNumber from 'lodash/isNumber';
import filter from 'lodash/filter';
import PropTypes from 'prop-types';

import { Button, Radio, makeStyles } from '@material-ui/core';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';

import { getI18n } from "../../../utils/intl";

import LastDateRangeFilter from './LastDateRangeFilter';
import CustomDatePicker from './CustomDatePicker';
import CustomDateInputs from './CustomDateInputs';

import styles from './styles';

const dateRangeValueLabelMap = {
  HOUR_1: {
    label: '1 hour',
    value: '1',
    unit: 'hour',

  },
  HOUR_24: {
    label: '24 hours',
    value: '1',
    unit: 'day',
  },
  DAY_7: {
    label: '7 Days',
    value: '1',
    unit: 'week',
  },
  CUSTOM_DAY: {
    label1: 'Day',
    label2: 'Days',
    unit: 'day',
  },
};

const defaultCustomDays = 30;

const isDateRangeExceedLimit = (startDate, endDate) => {
  return (endDate - startDate) / 1000 / 60 / 60 / 24 > 731;
};

const handleChange = value => {
  const error = {
    typeError: false,
    rangeError: false,
  };
  if (value === '') {
    error.typeError = true;
  }
  if (Number.isNaN(Number(value))) {
    error.typeError = true;
  }
  if (value > 731 || value < 1 || !Number.isInteger(Number(value))) {
    error.rangeError = true;
  }
  return error;
};

const CustomDateRangeFilter = (props) => {
  const { selectedDate, hideLastHourOption, largeButton,
    handleDateRangeChange: handleDateRangeChangeProp,
  } = props;
  
  const intl = getI18n(useIntl());
  const useStyles = makeStyles(styles);
  const classes = useStyles();

  const [state, updateState] = useState({
    dateRange: 'DAY_7',
    selectedDateRange: 'DAY_7',
    isOpen: false,
    selectedCustomDays: defaultCustomDays,
    customDays: defaultCustomDays,
    disableApplyBtn: true,
    isCustomDateInputsEnabled: false,
    customEndDate: moment(),
    customStartDate: moment().subtract(30, 'days'),
    isCustomDateInputsApplied: false,
    rangeError: false,
    typeError: false,
  });

  const stateBackupRef = useRef(state);

  const setState = (value) => {
    if (typeof value === 'function') {
      updateState(currentValue => ({ ...currentValue, ...value(state) }));
    } else {
      updateState(currentValue => ({ ...currentValue, ...value }));
    }
  }

  useEffect(() => {
    if (!isEmpty(selectedDate)) {
      setSelectedDate(selectedDate);
    }
  }, []);

  const setSelectedDate = date => {
    if (!isUndefined(date.last)) {
      const dateRange = date.last;
      let selectedDateRange = date.last;
      if (!['HOUR_1', 'HOUR_24', 'DAY_7'].includes(selectedDateRange)) {
        selectedDateRange = 'LAST_DAYS';
      }
      if (isNumber(date.last)) {
        setState({
          selectedCustomDays: date.last,
          customDays: `${date.last}`,
        });
      }
      setState({
        dateRange,
        selectedDateRange,
      });
    } else if (
      !isUndefined(date.customStartDate) &&
      !isUndefined(date.customEndDate)
    ) {
      const { customStartDate, customEndDate } = date;
      setState({
        dateRange: 'custom',
        selectedDateRange: 'custom',
        customStartDate,
        customEndDate,
        isCustomDateInputsEnabled: true,
      });
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown, false);
    document.addEventListener('mousedown', handleMouseDown, false);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown, false);
      document.removeEventListener('mousedown', handleMouseDown, false);
    };
  }, []);

  const onDateRangeFilterClick = () => {
    if (!state.isOpen) {
      const stateBackup = JSON.parse(JSON.stringify(state));
      stateBackup.customStartDate = moment(stateBackup.customStartDate);
      stateBackup.customEndDate = moment(stateBackup.customEndDate);
      stateBackupRef.current = stateBackup;
      setState({ isOpen: true });
    } else if (stateBackupRef.current) {
      setState(stateBackupRef.current);
    }
  };

  const onApply = () => {
    const dateRange = state.selectedDateRange;
    let dateRangeValue;
    if (dateRange === 'custom') {
      const { customStartDate, customEndDate } = state;
      let startDate = moment(customStartDate).format('YYYY-MM-DD');
      let endDate = moment(customEndDate).format('YYYY-MM-DD');
      dateRangeValue = {
        customDays: true,
        customDates: { startDate, endDate },
      };
      startDate = moment(customStartDate).format('dddd, MMMM D, YYYY');
      endDate = moment(customEndDate).format('dddd, MMMM D, YYYY');
    } else if (!dateRangeValueLabelMap[dateRange]) {
      const customLastDays = dateRangeValueLabelMap.CUSTOM_DAY;
      dateRangeValue = {
        customDays: true,
        value: state.selectedCustomDays,
        unit: customLastDays.unit,
      };
    } else {
      dateRangeValue = {
        customDays: false,
        value: dateRange,
        unit: dateRangeValueLabelMap[dateRange].unit,
      };
    }
    setState({
      isOpen: false,
      dateRange: state.selectedDateRange,
      customDays: state.selectedCustomDays,
      isCustomDateInputsApplied: state.isCustomDateInputsEnabled,
    });
    handleDateRangeChangeProp(dateRangeValue);
  };

  const onCancel = () => {
    setState({ ...stateBackupRef.current, isOpen: false });
  };

  const onCustomDatesSelection = () => {
    handleDateRangeChange('custom', 0, false);
  };

  const onCustomDatesChange = customDates => {
    setState({
      customEndDate: customDates.endDate,
      customStartDate: customDates.startDate,
    });
    const disableApplyBtn =
      isNull(customDates.endDate) || isNull(customDates.startDate)
      || isDateRangeExceedLimit(customDates.startDate, customDates.endDate);
    handleDateRangeChange('custom', 0, disableApplyBtn);
  };

  const getSelectedDateLabel = () => {
    const { dateRange, customStartDate, customEndDate } = state;
    const lastLabel = intl.getI18nMessage(
      'label.analyticsDashboard.filter.dateRange.last.label',
    );
    let btnLabel = `${lastLabel} `;
    if (dateRange === 'custom') {
      const startDate = moment(customStartDate).format(
        'dddd, MMMM D, YYYY',
      );
      const endDate = moment(customEndDate).format('dddd, MMMM D, YYYY');
      btnLabel = `${startDate} - ${endDate}`;
    } else if (!dateRangeValueLabelMap[dateRange]) {
      const customLastDays = dateRangeValueLabelMap.CUSTOM_DAY;
      const dateRangeSuffix =
        (state.selectedCustomDays) > 1 ? customLastDays.label2 : customLastDays.label1;
      btnLabel += `${state.selectedCustomDays} ${dateRangeSuffix}`;
    } else {
      btnLabel += dateRangeValueLabelMap[dateRange].label;
    }
    return btnLabel;
  };

  const handleKeyDown = event => {
    if (event.keyCode === 27 && state.isOpen) {
      setState(stateBackupRef.current);
    }
  };

  const handleMouseDown = event => {
    if (event.which === 1 && state.isOpen) {
      const targetElement = event.target;
      const datePickerContainer = targetElement.closest('#date-range-picker');
      const datePickerBtn = targetElement.closest('#date-picker-btn');
      if (isNull(datePickerBtn) && isNull(datePickerContainer)) {
        setState(stateBackupRef.current);
      }
    }
  };

  const handleEnterKey = () => {
    const dateRange = state.selectedDateRange;
    let newState = {
      selectedDateRange: dateRange,
      disableApplyBtn: false,
    };
    const err = handleChange(dateRange);
    if (err.rangeError || err.typeError) {
      newState.disableApplyBtn = err.rangeError || err.typeError;
      newState = { ...newState, ...err };
      setState(newState);
    } else {
      onApply();
    }
  };

  const handleDateRangeChange = (dateRangeParam, days, error) => {
    let newState = {
      selectedDateRange: dateRangeParam,
      disableApplyBtn: error,
    };
    if (dateRangeParam === 'LAST_DAYS') {
      const err = handleChange(days);
      if (err.rangeError || err.typeError) {
        newState.disableApplyBtn = err.rangeError || err.typeError;
      }
      newState = { ...newState, ...err };
      newState.selectedCustomDays = days;
    }
    newState.isCustomDateInputsEnabled = dateRangeParam === 'custom';
    setState(newState);
  };

  const { customStartDate, customEndDate } = state;
  const startDateString = moment(customStartDate).format(
    'dddd, MMMM D, YYYY',
  );
  const endDateString = moment(customEndDate).format('dddd, MMMM D, YYYY');
  const btnLabel = getSelectedDateLabel();
  
  const data = [
    { name: intl.getI18nMessage('label.analyticsDashboard.filter.dateRange.last.choice.oneHr'), value: 'HOUR_1' },
    { name: intl.getI18nMessage('label.analyticsDashboard.filter.dateRange.last.choice.twentyFourHrs'), value: 'HOUR_24' },
    { name: intl.getI18nMessage('label.analyticsDashboard.filter.dateRange.last.choice.sevenDays'), value: 'DAY_7' },
    { name: '', value: 'LAST_DAYS' },
  ];

  const lastDateRangeFilterData = hideLastHourOption ? filter(data, item => item.value !== 'HOUR_1') : data;

  return (
    <div className={classes.popupContainer}>
      <div className={classes.filterTitle}>
        {intl.getI18nMessage('label.analyticsDashboard.filter.dateRange.label')}
      </div>
      <div id={'date-picker-btn'}>
        <Button
          className={largeButton ? clsx(classes.popupBtn, classes.largePopupBtn) : classes.popupBtn}
          onClick={onDateRangeFilterClick}
          endIcon={
            <ArrowDropDownIcon
              fontSize='small'
              className={clsx(classes.icon, { [classes.dropdownOpen]: state.isOpen })}
            />
          }
        >
          {btnLabel}
        </Button>
        <div className={classes.printDateRange}>
          <h5>{btnLabel}</h5>
        </div>
      </div>
      {state.isOpen && (
        <div className={classes.dateRangePicker} id={'date-range-picker'}>
          <div className={classes.filterContainer}>
            <span className={classes.dateRangeLabel}>
              {intl.getI18nMessage('label.analyticsDashboard.filter.dateRange.label')}
            </span>
            <LastDateRangeFilter
              data={lastDateRangeFilterData}
              handleDateRangeChange={handleDateRangeChange}
              dateRange={state.selectedDateRange}
              customDays={state.selectedCustomDays}
              handleEnterKey={handleEnterKey}
              rangeError={state.rangeError}
              typeError={state.typeError}
            />
            <span className={classes.customDates}>
              {intl.getI18nMessage('label.analyticsDashboard.filter.custom.dateRange.label')}
            </span>
            <div className={classes.customDatePickerWrapper}>
              <div className={classes.customDatePickerContainer}>
                <Radio
                  name={'customDatesInput'}
                  className={classes.customRadioBtn}
                  checked={state.isCustomDateInputsEnabled}
                  onChange={onCustomDatesSelection}
                />
                {state.isCustomDateInputsEnabled && (
                  <div className={classes.datesPrefix}>From</div>
                )}
              </div>
              {!state.isCustomDateInputsEnabled && (
                <CustomDateInputs
                  startDateString={startDateString}
                  endDateString={endDateString}
                />
              )}
              {state.isCustomDateInputsEnabled && (
                <div>
                  <CustomDatePicker
                    autoFocusEndDate={false}
                    initialStartDate={customStartDate}
                    initialEndDate={customEndDate}
                    onCustomDatesChange={onCustomDatesChange}
                  />
                </div>
              )}
            </div>
          </div>
          <div className={classes.buttonBar}>
            {isDateRangeExceedLimit(customStartDate, customEndDate) &&
              <div className={classes.smallLabel}>
                {intl.getI18nMessage('label.analytics.print.filters.date.custom.exceeds.error')}
              </div>
            }
            <Button
              onClick={onApply}
              className={
                state.disableApplyBtn
                  ? clsx(classes.applyBtn, classes.button, classes.disableBtn)
                  : clsx(classes.applyBtn, classes.button, classes.disabled)
              }
              disabled={state.disableApplyBtn}
            >
              {intl.getI18nMessage('label.analyticsDashboard.filter.dateRange.button.apply')}
            </Button>
            <Button
              className={clsx(classes.cancelBtn, classes.button)}
              primary
              onClick={onCancel}
            >
              {intl.getI18nMessage('label.analyticsDashboard.filter.dateRange.button.cancel')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

CustomDateRangeFilter.propTypes = {
  hideLastHourOption: PropTypes.bool,
  selectedDate: PropTypes.shape(),
  handleDateRangeChange: PropTypes.func,
  largeButton: PropTypes.bool,
};

export default CustomDateRangeFilter;